import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { toast } from "sonner";
import {
  Building2,
  Users,
  Briefcase,
  Home,
  RefreshCw,
  TrendingUp,
  MapPin,
  ChevronRight,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const RegionalDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/regional/dashboard`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load regional dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatK = (n) => `K ${(n || 0).toLocaleString()}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return <div className="p-6 text-center text-muted-foreground">No regional data available.</div>;
  }

  const s = data.summary;

  return (
    <div className="space-y-6" data-testid="regional-dashboard">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
        <div>
          <h1 className="text-2xl font-bold">{data.region.name} Region</h1>
          <p className="text-muted-foreground text-sm">
            Code: {data.region.code} · Regional Manager: {user?.name}
          </p>
          {data.region.description && (
            <p className="text-sm text-muted-foreground mt-1">{data.region.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Link to="/dashboard/regional/report">
            <Button>
              <TrendingUp className="w-4 h-4 mr-2" />
              Regional Report
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={<Building2 className="w-5 h-5" />} label="Branches" value={s.branch_count} color="bg-blue-500" />
        <Kpi icon={<Users className="w-5 h-5" />} label="Total Team" value={s.team_size} color="bg-emerald-500" />
        <Kpi icon={<Home className="w-5 h-5" />} label="Active Rentals" value={s.active_rentals} color="bg-purple-500" />
        <Kpi icon={<Briefcase className="w-5 h-5" />} label="Revenue" value={formatK(s.total_revenue)} color="bg-amber-500" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Total Properties" value={s.total_properties} />
        <Stat label="Open Pipeline" value={formatK(s.pipeline_value)} />
        <Stat label="Total Deals" value={s.total_deals} />
        <Stat label="Deals Won" value={s.deals_won} />
      </div>

      {/* Monthly trend */}
      {data.monthly_trend && data.monthly_trend.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Region Revenue (Last 6 Months)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `K${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatK(v)} />
                <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Per-branch breakdown */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Branches in {data.region.name}</CardTitle>
          <Link to="/dashboard/regional/branches">
            <Button variant="ghost" size="sm">
              View All
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {data.branches.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              No branches in this region yet. Ask the General Manager to assign branches.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3">Branch</th>
                    <th className="text-center p-3">Team</th>
                    <th className="text-center p-3">Properties</th>
                    <th className="text-center p-3">Deals Won</th>
                    <th className="text-right p-3">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.branches.map((b) => (
                    <tr key={b.branch_id} className="border-b hover:bg-gray-50" data-testid={`branch-row-${b.branch_id}`}>
                      <td className="p-3">
                        <div className="font-medium flex items-center gap-1">
                          {b.name}
                          <span className="text-xs text-muted-foreground">({b.code})</span>
                        </div>
                        {b.city && <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{b.city}</div>}
                      </td>
                      <td className="p-3 text-center">{b.team_size}</td>
                      <td className="p-3 text-center">{b.properties}</td>
                      <td className="p-3 text-center text-green-600 font-medium">{b.deals_won}</td>
                      <td className="p-3 text-right font-semibold">{formatK(b.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const Kpi = ({ icon, label, value, color }) => (
  <Card>
    <CardContent className="pt-4">
      <div className="flex items-center gap-3">
        <div className={`${color} text-white p-2 rounded-lg`}>{icon}</div>
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-sm text-muted-foreground">{label}</div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const Stat = ({ label, value }) => (
  <Card>
    <CardContent className="pt-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-xl font-bold mt-1">{value}</div>
    </CardContent>
  </Card>
);

export default RegionalDashboard;
